import React, { useContext } from 'react';
import I18n from 'i18n-js';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useApolloClient } from '@apollo/client';
import { RCMenu, RCIcon } from '@reverbdotcom/cadence/components';
import { TrashIcon } from '@reverbdotcom/cadence/icons/react';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { CoreListingDeleteDraft, Listing } from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import SellerListingsCollectionContext from '../SellerListingsCollectionContext';
import { ActionType } from '../sellerListingsCollectionReducer';

interface IDeleteButtonProps {
  setDraftDeleteErrors: React.Dispatch<React.SetStateAction<Array<string>>>
  listing: Listing;
  insightsVariant?: boolean;
}

export const COMPONENT_NAME = 'DeleteButton';

export default function DeleteButton({ listing, setDraftDeleteErrors, insightsVariant }: IDeleteButtonProps) {
  const apolloClient = useApolloClient();
  const [deleteDraftListing] = useMutation<
    CoreListingDeleteDraft.Mutation,
    CoreListingDeleteDraft.Variables
  >(DELETE_DRAFT_MUTATION);

  const {
    dispatch: selectionDispatch,
  } = useContext(SellerListingsCollectionContext);

  async function startDelete() {
    const confirmMessage = I18n.t(
      'discovery.listingsManagement.sellerListingCard.deleteDraftConfirm',
      {
        title: listing.title,
      },
    );

    if (!confirm(confirmMessage)) return;

    selectionDispatch({
      type: ActionType.LOAD_LISTINGS,
      payload: {
        listingIds: [listing.id],
      },
    });

    try {
      const result = await deleteDraftListing({
        variables: {
          input: {
            listingId: listing.id,
          },
        },
      });

      if (result.data) {
        selectionDispatch({
          type: ActionType.REMOVE_LISTINGS,
          payload: {
            listingIds: [listing.id],
          },
        });

        evictFromCache();
      } else if (result.errors && result.errors.length > 0) {
        selectionDispatch({
          type: ActionType.RESET_LOADING,
          payload: {
            listingIds: [listing.id],
          },
        });

        const [firstError] = result.errors;
        const parsedErrors = JSON.parse(firstError.message);
        setDraftDeleteErrors(parsedErrors);
      }
    } catch (error) {
      selectionDispatch({
        type: ActionType.RESET_LOADING,
        payload: {
          listingIds: [listing.id],
        },
      });
    }
  }

  function evictFromCache() {
    apolloClient.cache.evict({
      id: apolloClient.cache.identify({
        __typename: 'Listing',
        id: listing.id,
      }),
    });
    apolloClient.cache.gc();
  }

  function handleClick() {
    startDelete();
    trackEvent({
      eventName: MParticleEventName.ClickedComponent,
      componentName: COMPONENT_NAME,
      listingId: listing.id,
    });
  }

  if (insightsVariant) {
    return (
      <RCMenu.Item onClick={handleClick} destructive>
        {I18n.t('discovery.listingsManagement.sellerListingCard.deleteDraft')}
      </RCMenu.Item>
    );
  }

  return (
    <RCMenu.Item onClick={handleClick}>
      {I18n.t('discovery.listingsManagement.sellerListingCard.deleteDraft')}
      <RCIcon icon={<TrashIcon />} size="inline" spacing="left" />
    </RCMenu.Item>
  );
}

const DELETE_DRAFT_MUTATION = gql(`
  mutation Core_Listing_DeleteDraft(
    $input: Input_core_apimessages_DraftListingDeleteRequest
  ) {
    deleteDraftListing(input: $input) {
      listingId
    }
  }
`);
